import { catalog } from '../core/catalog.js';
import { settings } from '../core/storage.js';
import { device } from '../core/device.js';
import { imageUrl, enhance } from '../ui/render.js';
import { heroHtml, heroData, heroMetaHtml, heroIntervalMs } from './home.js';

/* Cabecera destacada de Películas y de Series: el mismo carrusel del Inicio, pero solo con títulos de ese tipo.
   Se puede quitar en Ajustes → Aspecto («Cabecera destacada»). */

const COUNT = 5;

export const libraryHeroEnabled = () => settings.get('libraryHero') !== false;

function pick(kind, items, n = COUNT) {
    const pool = (items || catalog.visible(kind)).filter((x) => x.backdrop || x.poster).slice(0, 400);
    if (!pool.length) return [];
    const score = (x) => (x.rating || 0) + (x.added ? 1 : 0) + (x.backdrop ? 2 : 0);
    const top = pool.map((item) => ({ kind, item })).sort((a, b) => score(b.item) - score(a.item)).slice(0, 30);
    for (let i = top.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [top[i], top[j]] = [top[j], top[i]];
    }
    return top.slice(0, Math.min(n, top.length));
}

/** Monta la cabecera en `host`. Devuelve { setVisible(bool), destroy() }. */
export function mountLibraryHero(host, kind) {
    let list = [];
    let index = 0;
    let timer = null;
    let alive = true;
    let shownOk = true;
    let items = null; // con filtros puestos: los títulos que se están viendo; sin ellos, todo el catálogo
    let poolKey = '';

    const bindImage = () => {
        const im = host.querySelector('.hero-img');
        if (!im) return;
        im.addEventListener('error', () => {
            const alt = im.getAttribute('data-fallback');
            im.removeAttribute('data-fallback');
            if (alt) im.src = alt; else im.remove();
        });
    };
    const paintHtml = () => {
        host.innerHTML = list.length && libraryHeroEnabled() ? heroHtml(list, index, { navDefault: false, lib: true }) : '';
    };
    const render = () => {
        // En la tele el foco se conserva al repintar (si no, saltaba a otro sitio y la página se desplazaba).
        if (device.isTV && window.IPTV && window.IPTV.tvnav && host.contains(document.activeElement)) window.IPTV.tvnav.preserve(paintHtml, host); else paintHtml();
        host.hidden = !shownOk || !host.innerHTML;
        bindImage();
        const next = list[(index + 1) % list.length];
        if (next) { const src = heroData(next).image; if (src) { const im = new Image(); im.src = imageUrl(src); } }
        enhance(host);
    };
    const go = (i, { manual = false } = {}) => {
        if (!list.length) return;
        index = ((i % list.length) + list.length) % list.length;
        render();
        if (manual) schedule();
    };
    const schedule = () => {
        clearInterval(timer);
        const every = heroIntervalMs();
        if (list.length < 2 || !every) return;
        timer = setInterval(() => {
            if (document.hidden || host.hidden || (window.IPTV.player && window.IPTV.player.isOpen)) return;
            const hero = host.querySelector('.hero');
            if (!hero || hero.matches(':hover') || hero.querySelector('.is-focused')) return;
            go(index + 1);
        }, every);
    };
    // La lista del proveedor casi nunca trae fondo ni sinopsis: se piden las fichas de los destacados (con caché) y se completa lo que se ve.
    const loadDetails = () => {
        list.forEach((f) => {
            const job = kind === 'vod' ? catalog.vodInfo(f.item.id) : catalog.seriesInfo(f.item.id);
            Promise.resolve(job).then((info) => {
                if (!alive || !info || list.indexOf(f) === -1) return;
                const before = heroData(f);
                f.info = info;
                if (list[index] !== f) return;
                const after = heroData(f);
                const meta = host.querySelector('[data-hero-meta]');
                if (meta) meta.innerHTML = heroMetaHtml(after);
                if (after.image !== before.image || (after.plot && !before.plot)) render();
            }).catch(() => {});
        });
    };
    const valid = () => list.length > 0 && list.every((f) => catalog.get(kind, f.item.id));
    const build = () => {
        if (!catalog.loaded) return;
        if (valid()) { if (host.innerHTML === '' && libraryHeroEnabled()) render(); return; }
        list = pick(kind, items);
        index = 0;
        render();
        schedule();
        loadDetails();
    };

    const onClick = (e) => {
        const step = e.target.closest('[data-hero-step]');
        if (step) { e.preventDefault(); go(index + Number(step.getAttribute('data-hero-step')), { manual: true }); return; }
        const dot = e.target.closest('[data-hero-go]');
        if (dot) { e.preventDefault(); go(Number(dot.getAttribute('data-hero-go')), { manual: true }); return; }
        const play = e.target.closest('[data-hero-play]');
        if (play) {
            e.preventDefault();
            const [k, id] = play.getAttribute('data-hero-play').split(':');
            window.IPTV.actions.playFromCatalog(k, id);
        }
    };
    host.addEventListener('click', onClick);
    // Deslizar para cambiar de destacado (táctil).
    let swipe = null;
    host.addEventListener('touchstart', (e) => {
        const tch = e.touches[0];
        swipe = tch && e.touches.length === 1 ? { x: tch.clientX, y: tch.clientY, t: Date.now() } : null;
    }, { passive: true });
    host.addEventListener('touchend', (e) => {
        if (!swipe) return;
        const tch = e.changedTouches[0];
        const dx = tch.clientX - swipe.x;
        const dy = tch.clientY - swipe.y;
        const fast = Date.now() - swipe.t < 800;
        swipe = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && fast) go(index + (dx < 0 ? 1 : -1), { manual: true });
    }, { passive: true });

    build();
    return {
        build,
        /** Se esconde mientras hay búsqueda; con filtros (proveedor, categoría…) destaca títulos de lo que se está viendo. */
        update({ visible, titles }) {
            shownOk = !!visible;
            if (shownOk && titles) {
                const key = titles.length + ':' + (titles[0] ? titles[0].id : '') + ':' + (titles[titles.length - 1] ? titles[titles.length - 1].id : '');
                if (key !== poolKey) {
                    poolKey = key;
                    items = titles.length >= 1 ? titles : null;
                    list = [];
                    build();
                }
            }
            if (shownOk && !host.innerHTML && list.length && libraryHeroEnabled()) render();
            host.hidden = !shownOk || !host.innerHTML;
        },
        destroy() {
            alive = false;
            clearInterval(timer);
            host.removeEventListener('click', onClick);
        }
    };
}
